import { Connection, PublicKey } from '@solana/web3.js'

import { createContextLogger } from '@src/utils'

const logger = createContextLogger('solana.api')

// Cache for token metadata to avoid repeated API calls
const tokenMetadataCache: Record<
    string,
    {
        name?: string
        symbol?: string
        logoURI?: string
        decimals?: number
    }
> = {}

/**
 * Fetches token metadata for a Solana token using Jupiter Data API
 * @param tokenAddress The token address
 * @returns The token metadata or undefined if not found
 */
export async function getTokenMetadata(tokenAddress: string): Promise<
    | {
          name?: string
          symbol?: string
          logoURI?: string
          decimals?: number
      }
    | undefined
> {
    try {
        // Check cache first
        if (tokenMetadataCache[tokenAddress]) {
            return tokenMetadataCache[tokenAddress]
        }

        // Use Jupiter Data API to get token info
        try {
            const jupiterResponse = await fetch(`https://datapi.jup.ag/v1/pools?assetIds=${tokenAddress}`)
            const jupiterData = await jupiterResponse.json()

            if (jupiterData.pools && jupiterData.pools.length > 0) {
                const pool = jupiterData.pools[0]

                if (pool.baseAsset && pool.baseAsset.id === tokenAddress) {
                    const result = {
                        name: pool.baseAsset.name,
                        symbol: pool.baseAsset.symbol,
                        logoURI: pool.baseAsset.icon,
                        decimals: pool.baseAsset.decimals,
                    }

                    // Cache the result
                    tokenMetadataCache[tokenAddress] = result
                    return result
                }
            }
        } catch (error) {
            logger.error('Error fetching from Jupiter Data API:', error)
        }

        // If Jupiter Data API fails, try token.jup.ag as a backup
        try {
            const jupiterTokenResponse = await fetch(`https://token.jup.ag/all`)
            const jupiterTokens = await jupiterTokenResponse.json()

            const token = jupiterTokens.find((t: any) => t.address === tokenAddress)

            if (token) {
                const result = {
                    name: token.name,
                    symbol: token.symbol,
                    logoURI: token.logoURI,
                    decimals: token.decimals,
                }

                // Cache the result
                tokenMetadataCache[tokenAddress] = result
                return result
            }
        } catch (error) {
            logger.error('Error fetching from Jupiter token API:', error)
        }

        // As a last resort, try to get basic info from on-chain data
        try {
            const connection = new Connection('https://api.mainnet-beta.solana.com')
            const mintInfo = await connection.getParsedAccountInfo(new PublicKey(tokenAddress))

            if (mintInfo.value && mintInfo.value.data) {
                const parsedData = (mintInfo.value.data as any).parsed

                if (parsedData && parsedData.info) {
                    const result = {
                        decimals: parsedData.info.decimals,
                        // Try common logo URL patterns
                        logoURI: `https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/${tokenAddress}/logo.png`,
                    }

                    // Cache the result
                    tokenMetadataCache[tokenAddress] = result
                    return result
                }
            }
        } catch (error) {
            logger.error('Error fetching on-chain data:', error)
        }

        return undefined
    } catch (error) {
        logger.error('Error in getTokenMetadata:', error)
        return undefined
    }
}
